import { Component, OnInit } from '@angular/core';
import { ExpertsHeaderComponent } from '../../components/experts-header/experts-header';
import { ExpertsStatsComponent } from '../../components/experts-stats/experts-stats';
import { ExpertsFiltersComponent, ExpertFilters } from '../../components/experts-filters/experts-filters';
import { ExpertCardComponent, Expert } from '../../components/expert-card/expert-card';
import { ExpertsService } from '../../services/experts.service';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

interface ExpertsPagination {
  pages: number;
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  nextPage: number | null;
  previousPage: number | null;
}

@Component({
  selector: 'app-main-page',
  imports: [
    ExpertsHeaderComponent,
    ExpertsStatsComponent,
    ExpertsFiltersComponent,
    ExpertCardComponent,
    TranslatePipe,
  ],
  templateUrl: './main-page.html',
  styleUrl: './main-page.scss',
})
export class MainPage implements OnInit {
  experts: Expert[] = [];
  filteredExperts: Expert[] = [];
  isLoading = false;
  errorMessage = '';

  pagination: ExpertsPagination = {
    pages: 1,
    currentPage: 1,
    totalItems: 0,
    itemsPerPage: 0,
    nextPage: null,
    previousPage: null,
  };

  stats = {
    totalExperts: 0,
    totalReviews: 0,
    averageRating: 0,
    certifiedExperts: 0,
  };

  currentFilters: ExpertFilters = {
    searchQuery: '',
    expertType: 'all',
    minRating: 0,
    sortBy: 'rating',
  };

  constructor(private expertsService: ExpertsService) { }

  ngOnInit(): void {
    this.loadExperts(1);
    this.loadStats();
  }

  loadStats(): void {
    this.expertsService.getStats().subscribe({
      next: (response) => {
        this.stats = {
          totalExperts: response.totalExperts || 0,
          totalReviews: response.totalReviews || 0,
          averageRating: response.averageRating || 0,
          certifiedExperts: response.trustedCount || 0,
        };
      },
      error: (error) => {
        console.error('Error loading experts stats:', error);
      },
    });
  }

  loadExperts(page = 1): void {
    this.isLoading = true;
    this.errorMessage = '';

    const selectedExpertType = this.currentFilters.expertType;
    const apiParams: any = {
      page,
      limit: 10,
    };

    if (this.currentFilters.searchQuery.trim()) {
      apiParams.search = this.currentFilters.searchQuery.trim();
    }

    if (selectedExpertType === 'developer') {
      apiParams.type = 'developer';
    } else if (selectedExpertType === 'broker' || selectedExpertType === 'broker_company') {
      apiParams.type = 'broker';
    }

    if (this.currentFilters.sortBy === 'rating') {
      apiParams.sort = 'highest_rated';
    } else if (this.currentFilters.sortBy === 'newest') {
      apiParams.sort = 'newest';
    }

    this.expertsService.getExperts(apiParams).subscribe({
      next: (response) => {
        console.log('API Response for experts:', response);
        const rawExperts = response?.data || [];
        this.experts = rawExperts.map((item: any) => this.mapApiExpertToFrontend(item));
        this.pagination = this.normalizePagination(response?.pagination, this.experts.length, page);
        this.applyLocalFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading experts from API:', error);
        this.isLoading = false;
        this.errorMessage = 'experts.errors.loadExperts';
        this.experts = [];
        this.filteredExperts = [];
        this.pagination = this.normalizePagination(null, 0, page);
      },
    });
  }

  onFiltersChanged(filters: ExpertFilters): void {
    this.currentFilters = filters;
    this.loadExperts(1);
  }

  goToPage(page: number | null): void {
    if (!page || page < 1 || page > this.pagination.pages || page === this.pagination.currentPage) {
      return;
    }

    this.loadExperts(page);
  }

  getVisiblePages(): number[] {
    const totalPages = this.pagination.pages || 1;
    const currentPage = this.pagination.currentPage || 1;
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    const pages: number[] = [];

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    return pages;
  }

  private applyLocalFilters(): void {
    let result = [...this.experts];

    if (this.currentFilters.minRating > 0) {
      result = result.filter((expert) => expert.rating >= this.currentFilters.minRating);
    }

    if (this.currentFilters.expertType !== 'all') {
      result = result.filter((expert) => expert.expertType === this.currentFilters.expertType);
    }

    const searchTerm = this.currentFilters.searchQuery.trim().toLowerCase();
    if (searchTerm) {
      result = result.filter((expert) =>
        [
          expert.name,
          expert.companyName,
          expert.email,
          expert.phoneNumber,
          expert.location,
          ...expert.tags,
          ...expert.serviceAreas,
          ...expert.languages,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchTerm)),
      );
    }

    if (this.currentFilters.sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (this.currentFilters.sortBy === 'reviews') {
      result.sort((a, b) => b.reviewsCount - a.reviewsCount);
    } else if (this.currentFilters.sortBy === 'deals') {
      result.sort((a, b) => b.dealsCount - a.dealsCount);
    } else if (this.currentFilters.sortBy === 'newest') {
      result.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
    }

    this.filteredExperts = result;
  }

  private mapApiExpertToFrontend(item: any): Expert {
    const experienceYears = this.normalizeExperienceYears(item.yearsOfExperience);
    const expertType = this.normalizeExpertType(item);
    const typeName = this.getExpertTypeName(expertType);
    const locations = this.toStringArray(item.locations);
    const serviceAreas = this.toStringArray(item.serviceAreas);
    const location = locations.length > 0 ? locations.join(', ') : serviceAreas.length > 0 ? serviceAreas.join(', ') : 'experts.common.notSpecified';
    const name = item.name || item.companyName || [item.firstName, item.lastName].filter(Boolean).join(' ') || 'Expert';
    const about = this.getLocalizedText(item.about);
    const dealsOrProjectsCount = Number(item.dealsOrProjectsCount || 0);
    const unitsCount = Number(item.unitsCount || 0);
    const tags = [...this.toStringArray(item.tags), ...this.toStringArray(item.specializations)].filter(
      (value, index, array) => array.indexOf(value) === index,
    );

    return {
      id: item._id || item.id,
      userId: item.userId,
      profileId: item.profileId,
      name,
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      phoneNumber: item.phoneNumber,
      companyName: item.companyName,
      website: item.website,
      image: item.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=e45705&color=fff&size=150`,
      type: item.type === 'developer' ? 'developer' : 'broker',
      expertType,
      brokerType: item.brokerType,
      typeName,
      typeNameKey: `experts.types.${expertType}`,
      role: item.role,
      rating: Number(item.averageRating || 0),
      reviewsCount: Number(item.totalReviews || 0),
      experienceYears,
      propertiesCount: unitsCount,
      dealsCount: dealsOrProjectsCount,
      ratingScore: Number(item.averageRating || 0),
      tags,
      location,
      serviceAreas,
      languages: this.toStringArray(item.languages),
      about,
      profileUrl: item.profileUrl,
      createdAt: item.createdAt,
      isVerified: !!item.isTrusted,
      isNew: !!item.isNew,
    };
  }

  private normalizeExperienceYears(value: unknown): number {
    const parsed = parseInt(String(value || 0), 10);
    if (Number.isNaN(parsed)) return 0;

    return parsed > 1900 ? new Date().getFullYear() - parsed : parsed;
  }

  private normalizePagination(
    pagination: Partial<ExpertsPagination> | null | undefined,
    fallbackItemsCount: number,
    fallbackPage: number,
  ): ExpertsPagination {
    return {
      pages: Math.max(1, Number(pagination?.pages || 1)),
      currentPage: Number(pagination?.currentPage || fallbackPage || 1),
      totalItems: Number(pagination?.totalItems || fallbackItemsCount),
      itemsPerPage: Number(pagination?.itemsPerPage || fallbackItemsCount),
      nextPage: pagination?.nextPage ?? null,
      previousPage: pagination?.previousPage ?? null,
    };
  }

  private normalizeExpertType(item: any): Expert['expertType'] {
    if (item.expertType === 'developer' || item.type === 'developer') return 'developer';
    if (item.expertType === 'broker_company' || item.brokerType === 'company') return 'broker_company';
    return 'broker';
  }

  private getExpertTypeName(expertType: Expert['expertType']): string {
    switch (expertType) {
      case 'developer':
        return 'experts.types.developer';
      case 'broker_company':
        return 'experts.types.broker_company';
      case 'broker':
      default:
        return 'experts.types.broker';
    }
  }

  private toStringArray(value: unknown): string[] {
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : [];
  }

  private getLocalizedText(value: any): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return value.ar || value.en || undefined;
  }
}
